import { Box, Typography, Chip } from '@mui/material';
import { SimpleShowLayout, TextField, FunctionField, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import { DrawerLinkWithData } from '@/components/global/drawers/DrawerLinkWithData';
import { requiredOperators } from '@/components/collectionPoint/OperatorPointIdInput.jsx';
import ConditionalFieldDisplay from './ConditionalFieldDisplay';
import OpeningHoursDisplay from './OpeningHoursDisplay';
import DetailsMap from '@/components/global/map/DetailsMap.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { forwarderTypeChoices } from '@/components/global/options/selectOptions.jsx';

const CollectionPointDetailsFields = ({ collectionPoint, isAdminOrBok }) => {
    const translate = useTranslate();

    return (
        <>
            <SimpleShowLayout>
                <TextField source='customId' label={translate('resources.collectionPoint.fields.customId')} />
            </SimpleShowLayout>
            <SimpleShowLayout>
                <Box label={translate('common.contact')}>
                    <TextField source='contactName' sx={{ display: 'block' }} />
                    <TextField source='contactEmail' sx={{ display: 'block' }} />
                    <TextField source='contactPhoneNumber' sx={{ display: 'block' }} />
                    <TextField source='employeePhoneNumber' sx={{ display: 'inline-block' }} />
                    {collectionPoint.employeePhoneNumber && (
                        <Typography variant='caption' className='no-bold' sx={{ display: 'inline-block', marginLeft: '4px' }}>
                            ({translate('resources.collectionPoint.labels.employeePhoneNumberLabel')})
                        </Typography>
                    )}
                </Box>
            </SimpleShowLayout>
            <SimpleShowLayout>
                <Box label={translate('common.address')}>
                    <Typography variant='caption'>
                        {collectionPoint.address}, {collectionPoint.postalCode} {collectionPoint.city}
                    </Typography>
                </Box>
            </SimpleShowLayout>
            <SimpleShowLayout sx={{ '& .MuiStack-root.ra-field': { borderBottom: 'none !important' } }}>
                <Box label={translate('resources.collectionPoint.labels.coordinatesSection')}>
                    <Typography variant='caption'>
                        {collectionPoint.latitude && collectionPoint.longitude ? (
                            collectionPoint.latitude + ', ' + collectionPoint.longitude
                        ) : (
                            <Typography variant='caption' style={{ color: Palette.gray.gray400 }}>
                                {translate('common.shortage')}
                            </Typography>
                        )}
                    </Typography>
                </Box>
            </SimpleShowLayout>
            <DetailsMap record={collectionPoint} collectionPoint={collectionPoint} />
            <SimpleShowLayout sx={{ '& .MuiStack-root.ra-field': { borderTop: 'none !important' } }}>
                <OpeningHoursDisplay collectionPoint={collectionPoint} />
            </SimpleShowLayout>
            <SimpleShowLayout>
                <Box label={translate('menu.devices')}>
                    {collectionPoint.devices?.map(item => (
                        <DrawerLink
                            key={item.id}
                            resource='device'
                            source={item.id}
                            action='show'
                            relative
                            label={<Typography variant='caption'>{item.serialNumber}</Typography>}
                            sx={{ display: 'block' }}
                        />
                    ))}
                </Box>
            </SimpleShowLayout>
            <SimpleShowLayout>
                <TextField source='shopName' label={translate('resources.collectionPoint.fields.shopName')} />
            </SimpleShowLayout>
            <SimpleShowLayout>
                <TextField source='shopLabel' label={translate('resources.collectionPoint.fields.shopLabel')} />
            </SimpleShowLayout>
            <SimpleShowLayout>
                <ConditionalFieldDisplay label={translate('resources.collectionPoint.labels.couponSourceSection')} value={collectionPoint.couponSource} />
            </SimpleShowLayout>
            {isAdminOrBok && (
                <SimpleShowLayout>
                    <NipField label={translate('resources.collectionPoint.form.nip')} collectionPoint={collectionPoint} />
                </SimpleShowLayout>
            )}
            {isAdminOrBok && (
                <SimpleShowLayout>
                    <ForwarderTypeField label={translate('resources.collectionPoint.form.forwarderType')} collectionPoint={collectionPoint} />
                </SimpleShowLayout>
            )}
            <SimpleShowLayout>
                <ConditionalFieldDisplay label={translate('common.operator')} value={collectionPoint.operator} />
            </SimpleShowLayout>
            {requiredOperators.includes(collectionPoint.operator) && (
                <SimpleShowLayout>
                    <ConditionalFieldDisplay label={translate('resources.collectionPoint.labels.collectionPointId')} value={collectionPoint.operatorPointId} />
                </SimpleShowLayout>
            )}
            {isAdminOrBok && (
                <SimpleShowLayout>
                    <Box
                        label={translate('resources.collectionPoint.form.serviceCompany')}
                        sx={{ '& > *': { display: 'inline !important', '&.MuiChip-root': { marginLeft: '8px' } } }}
                    >
                        <FunctionField
                            source='serviceCompanyId'
                            label={translate('resources.collectionPoint.form.serviceCompany')}
                            render={record => (
                                <DrawerLinkWithData
                                    record={record}
                                    source='serviceCompanyId'
                                    resource='service-company'
                                    labelField='name'
                                    sx={{ fontWeight: '600 !important' }}
                                />
                            )}
                        />
                        {collectionPoint.premiumService && (
                            <Chip size='small' label={translate('resources.device.labels.premiumService')} className='pos-field-name-chip chip-small' />
                        )}
                    </Box>
                </SimpleShowLayout>
            )}
        </>
    );
};

const NipField = ({ label, collectionPoint }) => {
    const translate = useTranslate();
    const value = collectionPoint.nip || collectionPoint?.contractor?.nip;

    if (!collectionPoint.nip) {
        return (
            <Box label={label}>
                <Typography variant='caption' style={{ color: Palette.gray.gray400 }}>
                    {translate('resources.collectionPoint.labels.nipFromContractor', { nip: value })}
                </Typography>
            </Box>
        );
    }

    return (
        <Box label={label}>
            <Typography variant='caption'>{value}</Typography>
        </Box>
    );
};

const ForwarderTypeField = ({ label, collectionPoint }) => {
    const translate = useTranslate();
    const value = collectionPoint.forwarderType ?? collectionPoint?.contractor?.forwarderType;
    const valueText = forwarderTypeChoices(translate).find(choice => choice.id === value)?.name || value;

    if (!collectionPoint.forwarderType) {
        return (
            <Box label={label}>
                <Typography variant='caption' style={{ color: Palette.gray.gray400 }}>
                    {translate('resources.collectionPoint.labels.forwarderTypeFromContractor', { forwarderType: valueText })}
                </Typography>
            </Box>
        );
    }

    return (
        <Box label={label}>
            <Typography variant='caption'>{valueText}</Typography>
        </Box>
    );
};

NipField.propTypes = {
    label: PropTypes.string.isRequired,
    collectionPoint: PropTypes.shape({
        nip: PropTypes.string,
        contractor: PropTypes.shape({
            nip: PropTypes.string,
        }),
    }),
};

ForwarderTypeField.propTypes = {
    label: PropTypes.string.isRequired,
    collectionPoint: PropTypes.shape({
        forwarderType: PropTypes.string,
        contractor: PropTypes.shape({
            forwarderType: PropTypes.string,
        }),
    }),
};

CollectionPointDetailsFields.propTypes = {
    collectionPoint: PropTypes.shape({
        address: PropTypes.string,
        postalCode: PropTypes.string,
        city: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        devices: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                serialNumber: PropTypes.string,
            }),
        ),
        couponSource: PropTypes.string,
        operator: PropTypes.string,
        operatorPointId: PropTypes.string,
        premiumService: PropTypes.bool,
        serviceCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    isAdminOrBok: PropTypes.bool.isRequired,
};

export default CollectionPointDetailsFields;
